import { useState } from "react"
import { isAddress } from "@ethersproject/address"
import type { BigNumberish } from "@ethersproject/bignumber"
import type { FetchSignerResult, Signer } from "@wagmi/core"
import { ethers } from "ethers"
import { useQueryClient } from "wagmi"
import isEqual from "lodash.isequal"
import { captureMessage } from "@sentry/nextjs"

import type { Governor, VoteType } from "query/graphql"
import {
  useCreateVoteAttemptMutation,
  GovernorType,
  AccountType,
} from "query/graphql"
import { useMultichainAccount } from "web3/hooks/useMultichainAccount"
import type { UseCorrectChainErrors } from "web3/helpers/chain"
import { getCorrectChain } from "web3/helpers/chain"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import { getGovernorOpenZeppelin } from "web3/hooks/useGovernorOpenZeppelin"
import { getAccountIdParams, isValidAccountId } from "web3/helpers/accountId"
import { SUPPORT_NUMBERS } from "voting/constants/support"
import { useModals } from "common/hooks/useModals"
import { useSignTransaction } from "web3/hooks/useSignTransaction"
import type { RelayerTransactionResponse } from "web3/hooks/useRelayer"
import { useRelayer } from "web3/hooks/useRelayer"
import { useToast } from "common/hooks/useToast"
import { useAddress } from "web3/hooks/useAddress"
import { normalize } from "common/helpers/string"
import { isSameAddress } from "web3/helpers/address"

type Values = {
  error: Error | undefined
  isLoading: boolean
  castVoteBySig: (args: {
    reason?: string
    signer?: FetchSignerResult<Signer>
    support: VoteType
    governance: Governor
    proposalId: BigNumberish
    onchainId: BigNumberish
  }) => Promise<void>
}

enum UseCastVoteError {
  Signernotfound = "SIGNER_NOT_FOUND",
  Nonmatchingchain = "NON_MATCHING_CHAIN",
  Undefinedgovernance = "UNDEFINED_GOVERNANCE",
  Invalidgovernanceid = "INVALID_GOVERNANCE_ID",
  Invalidgovernoraddress = "INVALID_GOVERNOR_ADDRESS",
  InvalidSigner = "INVALID_SIGNER",
}

type Error = UseCorrectChainErrors | UseCastVoteError

type Props = {
  chainReference?: number
  isMultiChain?: boolean
}

export const useCastVoteBySig = ({
  chainReference,
  isMultiChain = false,
}: Props): Values => {
  const [error, setError] = useState<Error | undefined>(undefined)
  const userAddress = useAddress()
  const { multichainAccount } = useMultichainAccount()
  const { type } = multichainAccount

  const { toast } = useToast()
  const { signCastVoteTransaction } = useSignTransaction({
    chainId: chainReference,
  })
  const { submitCastVoteTransaction: submitTransaction } = useRelayer()
  const { voteModal } = useModals()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  const { mutate: createVoteAttempt } = useCreateVoteAttemptMutation({
    onSuccess: (data) => {
      if (data) {
        const queriesToInvalidate = ["ProposalVoteAttempt"]
        queriesToInvalidate.forEach((query) =>
          queryClient.invalidateQueries({
            queryKey: [query],
          }),
        )
      }
    },
  })

  const getVote = (support: VoteType) => SUPPORT_NUMBERS[support]

  const castVoteBySig = async ({
    signer,
    support,
    governance,
    proposalId,
    onchainId,
  }: {
    signer?: FetchSignerResult<Signer>
    support: VoteType
    governance: Governor
    proposalId: BigNumberish
    onchainId: BigNumberish
  }): Promise<void> => {
    if (!governance) {
      setError(UseCastVoteError.Undefinedgovernance)

      return
    }

    const { contracts, id: governanceId } = governance
    const { governor } = contracts
    const { type: governorType, address: governorAddress } = governor

    const { chainId } = getAccountIdParams(governance.id)
    const { reference } = getChainIdParams(chainId)
    const chainReferenceName = getChainReferenceName(reference)

    if (!signer) {
      setError(UseCastVoteError.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const governorOz = getGovernorOpenZeppelin(governorAddress, signer)

    // TODO(@nicolas): Pending
    // const governorAlpha = getGovernorAlpha(governorAddress, signer)
    // const governorBravo = getGovernorBravo(governorAddress, signer)
    // const governorAave = getGovernorAave(governorAddress, signer)

    const { isCorrectChain, error: useCorrectChainErrors } = getCorrectChain({
      chainId,
      chainReference,
    })

    if (!isCorrectChain) {
      setError(UseCastVoteError.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to delegate on this governace`,
        duration: 5000,
      })

      return
    }

    if (useCorrectChainErrors !== undefined) {
      setError(useCorrectChainErrors)

      throw new Error(
        `useCastVote => "chainId" or "chainReference" or both, are incorrect.
         This should not be happening as they are being gathered from the server and the provider`,
      )
    }

    if (!isAddress(governorAddress)) {
      setError(UseCastVoteError.Invalidgovernoraddress)

      return
    }

    if (!isValidAccountId(governanceId)) {
      setError(UseCastVoteError.Invalidgovernanceid)

      return
    }

    const fromAddress = await signer.getAddress()

    if (
      !isSameAddress(userAddress, fromAddress) ||
      isEqual(normalize(type), normalize(AccountType.Safe))
    ) {
      // For Safe wallets: The userAddress is the Safe contract address, while the signer
      // is always the EOA comming from the provider. These will never match.
      // Verify the signer matches the connected wallet address.
      setError(UseCastVoteError.InvalidSigner)
      toast({
        status: "warning",
        title: "Incorrect signer",
        description: `Signer is not the same as the connected address or the connected address is a Safe. Please connect to a non Safe wallet or uncheck Free Voting option`,
        duration: 5000,
      })

      captureMessage(
        `[useCastVoteBySig] Error: Invalid signer. userAddress=${userAddress} / signer=${fromAddress} / accountType=${type} / userId=${multichainAccount.id} / proposalId=${proposalId} / govId=${governance.id}`,
      )

      return
    }

    switch (governorType) {
      case GovernorType.Governoralpha:
        // TODO(@nicolas): Pending
        break

      case GovernorType.Nounsfork:
        break

      case GovernorType.Governorbravo:
      case GovernorType.Openzeppelingovernor:
        setIsLoading(true)
        const ozVote = getVote(support)

        const signature = await signCastVoteTransaction({
          contractAddress: governorOz.address,
          contractName: await governorOz.name(),
          proposalId: onchainId,
          support: ozVote,
          governorType: governor.type,
        })

        if (signature) {
          const { v, r, s } = ethers.utils.splitSignature(signature)

          const gasLimit = await governorOz.estimateGas.castVoteBySig(
            onchainId,
            ozVote,
            v,
            r,
            s,
          )

          const data = governorOz.interface.encodeFunctionData(
            "castVoteBySig",
            [onchainId, ozVote, v, r, s],
          )

          return submitTransaction({
            governanceId,
            address: fromAddress,
            proposalId: onchainId,
            support: ozVote,
            contractAddress: governorOz.address,
            contractName: await governorOz.name(),
            data,
            gasLimit: gasLimit.toNumber(),
            chainId,
            signature,
          })
            .then((data: RelayerTransactionResponse | undefined) => {
              if (data !== undefined) {
                if (data?.tx?.hash && proposalId && fromAddress && support) {
                  createVoteAttempt({
                    input: {
                      voter: fromAddress,
                      txHash: data.tx.hash,
                      proposalId: proposalId as string,
                      type: support,
                    },
                  })
                }

                if (!isMultiChain) {
                  voteModal.onClose()
                }
              }
            })
            .finally(() => {
              setIsLoading(false)
            })
        } else {
          setIsLoading(false)
        }

        break

      default:
        break
    }
  }

  return {
    error,
    castVoteBySig,
    isLoading,
  }
}
